/**
 * 채팅 기능 관련 API 통신 모듈
 * 
 * 이 모듈은 RAG 시스템의 채팅 기능과 관련된 모든 백엔드 API 통신을 처리합니다.
 * 일반 채팅, 스트리밍 채팅, 세션 관리 등 채팅 관련 모든 기능을 포함합니다.
 * 
 * 주요 기능:
 * - 일반/스트리밍 채팅 메시지 전송
 * - 채팅 세션 생성 및 관리
 * - 채팅 히스토리 조회
 */

import api from './api';

// 채팅 API 함수들
const chatApi = {
  /**
   * 일반 채팅 메시지 전송 (비스트리밍)
   * 
   * @param {Object} chatData - 채팅 요청 데이터
   * @param {Array} chatData.messages - 메시지 목록
   * @param {string} [chatData.kb_id] - 지식베이스 ID
   * @param {Array} [chatData.kb_ids] - 여러 지식베이스 ID 목록
   * @param {string} [chatData.model] - 사용할 LLM 모델
   * @param {number} [chatData.temperature] - 생성 온도 (0.0-1.0)
   * @param {boolean} [chatData.use_semantic_search] - 시맨틱 검색 사용 여부
   * @param {string} [chatData.session_id] - 채팅 세션 ID
   * @returns {Promise<Object>} 채팅 응답 (텍스트 및 소스 문서 포함)
   */
  sendChatMessage: async (chatData) => {
    const response = await api.post('/chat', chatData);
    return response.data;
  },
  
  /**
   * 스트리밍 채팅 메시지 전송 (스트리밍 응답)
   * 
   * SSE(Server-Sent Events)와 유사한 방식으로 스트리밍 응답을 처리합니다.
   * 긴 응답을 실시간으로 화면에 표시하기 위해 사용됩니다.
   * 
   * @param {Object} chatData - 채팅 요청 데이터 (sendChatMessage와 동일)
   * @param {Function} onMessage - 새 메시지 청크를 수신할 때 호출할 콜백 함수
   * @param {Function} onError - 오류 발생 시 호출할 콜백 함수
   * @param {Function} onComplete - 스트림이 완료되었을 때 호출할 콜백 함수
   * @returns {Object} 스트림 컨트롤러 (close 메서드 포함)
   */
  sendStreamingChatMessage: async (chatData, onMessage, onError, onComplete) => {
    try {
      // 요청에 타임스탬프를 추가하여 캐싱 방지
      const requestWithTimestamp = {
        ...chatData,
        timestamp: new Date().getTime() // 현재 시간을 밀리초로 추가
      };
      
      // fetch API를 사용하여 POST 요청 보내기
      const response = await fetch('/api/chat/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        body: JSON.stringify(requestWithTimestamp)
      });

      if (!response.ok) {
        throw new Error(`서버 오류: ${response.status} ${response.statusText}`);
      }

      // 응답을 읽을 수 있는 reader 생성
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      // EventSource와 유사하게 처리하기 위한 함수
      const processStream = async () => {
        let buffer = '';
        
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            if (onComplete) onComplete();
            break;
          }
          
          // 새 데이터를 버퍼에 추가
          buffer += decoder.decode(value, { stream: true });
          
          // 버퍼에서 완전한 메시지들을 추출
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || ''; // 마지막 불완전한 메시지를 버퍼에 남김
          
          // 각 완전한 메시지 처리
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const jsonStr = line.substring(6); // 'data: ' 제거
                const data = JSON.parse(jsonStr);
                onMessage(data);
              } catch (e) {
                console.error('JSON 파싱 오류:', e);
              }
            }
          }
        }
      };
      
      // 스트림 처리 시작
      processStream().catch(error => {
        if (onError) onError(error);
      });
      
      // 스트림 종료를 위한 함수 반환
      return {
        close: () => {
          reader.cancel();
          if (onComplete) onComplete();
        }
      };
    } catch (error) {
      if (onError) onError(error);
      return {
        close: () => {}
      };
    }
  },
  
  /**
   * 채팅 세션 생성
   * 
   * 세션을 생성하면 대화 내용을 유지하고 나중에 다시 접근할 수 있습니다.
   * 
   * @param {Object} sessionData - 세션 생성 데이터
   * @param {string} sessionData.name - 세션 이름
   * @param {string} [sessionData.kb_id] - 연결할 지식베이스 ID
   * @returns {Promise<Object>} 생성된 세션 정보
   */
  createSession: async (sessionData) => {
    const response = await api.post('/chat/sessions', sessionData);
    return response.data;
  },
  
  /**
   * 채팅 세션 목록 조회
   * 
   * 사용자가 생성한 모든 채팅 세션 목록을 가져옵니다.
   * 
   * @returns {Promise<Object>} 세션 목록 데이터
   */
  listSessions: async () => {
    const response = await api.get('/chat/sessions');
    return response.data;
  },
  
  /**
   * 특정 세션의 채팅 기록 조회
   * 
   * @param {string} sessionId - 조회할 세션 ID
   * @param {number} [limit=100] - 가져올 최대 메시지 수
   * @param {number} [offset=0] - 페이지네이션 오프셋
   * @returns {Promise<Object>} 세션 채팅 기록
   */
  getSessionHistory: async (sessionId, limit = 100, offset = 0) => {
    const response = await api.get(`/chat/sessions/${sessionId}/history`, {
      params: { limit, offset }
    });
    return response.data;
  },
  
  /**
   * 채팅 세션 삭제
   * 
   * @param {string} sessionId - 삭제할 세션 ID
   * @returns {Promise<Object>} 삭제 결과 데이터
   */
  deleteSession: async (sessionId) => {
    const response = await api.delete(`/chat/sessions/${sessionId}`);
    return response.data;
  }
};

export default chatApi; 